import React, { useState, useRef, useEffect } from 'react';
import { Cell, CellInput } from './tableStyles';
import { formatCellValue } from './tableUtils';

export const TableCell = ({
  value,
  formula,
  rowIndex,
  columnIndex,
  editable = false,
  onChange,
  onCellClick,
  emptyCellValue = '-',
  type = 'text',
  bold = false,
  italic = false,
  underline = false,
  strike = false,
  backgroundColor,
  textColor,
  isActive = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);

  const isEmpty = value === null || value === undefined || value === '';
  const isError = value === '#NAN' || value === '#ERROR' || value === '#CIRCULAR';
  const displayValue = isEmpty ? emptyCellValue : formatCellValue(value, type);

  // Build text decoration style
  const textDecorations = [];
  if (underline) textDecorations.push('underline');
  if (strike) textDecorations.push('line-through');

  // Build inline styles for formatting
  const textStyle = {
    ...(bold && { fontWeight: 700 }),
    ...(italic && { fontStyle: 'italic' }),
    ...(textDecorations.length > 0 && { textDecoration: textDecorations.join(' ') }),
    ...(textColor && { color: textColor }),
    ...(isError && { color: '#dc2626', fontWeight: 600 }), // Red color for errors
  };

  // Build cell-level styles
  const cellStyle = {
    ...(backgroundColor && { backgroundColor }),
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Notify parent of cell click
    if (onCellClick) {
      onCellClick(rowIndex, columnIndex);
    }

    if (editable) {
      // When editing, show the formula if it exists, otherwise show the value
      const editableValue = formula || String(value || '');
      setEditValue(editableValue);
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    if (isEditing) {
      if (onChange) {
        onChange(rowIndex, columnIndex, editValue);
      }
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <Cell isEditable={editable} isActive={isActive} style={cellStyle}>
        <CellInput
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus={true}
        />
      </Cell>
    );
  }

  const hasFormatting = bold || italic || underline || strike || textColor || isError;

  return (
    <Cell isEmpty={isEmpty} isEditable={editable} isActive={isActive} onClick={handleClick} style={cellStyle}>
      {hasFormatting ? <span style={textStyle}>{displayValue}</span> : displayValue}
    </Cell>
  );
};

// Memoized version for performance
export const MemoizedTableCell = React.memo(TableCell, (prev, next) => {
  return (
    prev.value === next.value &&
    prev.formula === next.formula &&
    prev.rowIndex === next.rowIndex &&
    prev.columnIndex === next.columnIndex &&
    prev.editable === next.editable &&
    prev.type === next.type &&
    prev.bold === next.bold &&
    prev.italic === next.italic &&
    prev.underline === next.underline &&
    prev.strike === next.strike &&
    prev.backgroundColor === next.backgroundColor &&
    prev.textColor === next.textColor &&
    prev.isActive === next.isActive
  );
});
